import { useMemo, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { MobileBottomBar } from "@/components/mobile/MobileBottomBar"
import type { MobileTopNavProps } from "@/components/mobile/MobileTopNav"
import { MobileTopNav } from "@/components/mobile/MobileTopNav"
import { CreateModal } from "@/components/modals/CreateModal"
import { ScrollToTop } from "@/components/shared/ScrollToTop"
import { ShareModal } from "@/components/shared/ShareModal"
import { useGlobalChatNotifications } from "@/features/chats/hooks/useGlobalChatNotifications"
import { NotificationsSheet } from "@/features/notifications/components/NotificationsSheet"
import { SearchOverlay } from "@/features/search/components/SearchOverlay"
import { getAssetUrl } from "@/utils/assets"
import { AppHeader } from "./AppHeader"

interface AppLayoutProps {
  children?: React.ReactNode
}

const DASHBOARD_ROUTES = [/^\/$/]

const ALT_BACKGROUND_ROUTES = [
  /^\/learn-and-practice/,
  /^\/connect/,
  /^\/my-rituality/,
  /^\/search/,
]

const MAIN_NAV_ROUTES = ["/", "/learn-and-practice", "/chats", "/connect", "/my-rituality"]

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const currentPath = location.pathname

  useGlobalChatNotifications()

  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  const handleSearchOpen = (open: boolean) => {
    if (open) {
      setNotificationsOpen(false)
      setCreateOpen(false)
    }
    setSearchOpen(open)
  }

  const handleNotificationsOpen = (open: boolean) => {
    if (open) {
      setSearchOpen(false)
      setCreateOpen(false)
    }
    setNotificationsOpen(open)
  }

  const handleCreateOpen = (open: boolean) => {
    if (open) {
      setSearchOpen(false)
      setNotificationsOpen(false)
    }
    setCreateOpen(open)
  }

  const isDashboardRoute = DASHBOARD_ROUTES.some(regex => regex.test(currentPath))
  const isAltRoute = ALT_BACKGROUND_ROUTES.some(regex => regex.test(currentPath))
  const showBackground = isDashboardRoute || isAltRoute
  const backgroundImage = isDashboardRoute
    ? getAssetUrl("background_image.jpg")
    : getAssetUrl("app-background.png")

  const isMyRitualityRoute =
    currentPath === "/my-rituality" || currentPath.startsWith("/my-rituality/")
  const isChatsRoute = currentPath === "/chats" || currentPath.startsWith("/chats/")

  const isMainNav = MAIN_NAV_ROUTES.some(route =>
    route === "/" ? currentPath === "/" : currentPath.startsWith(route),
  )

  const isRitualDetail = /^\/rituals\/[^/]+$/.test(currentPath)
  const isCommunityDetail = /^\/communities\/[^/]+$/.test(currentPath)
  const isProfilePage = currentPath === "/profile"
  const isOtherProfilePage = /^\/profile\/[^/]+$/.test(currentPath)

  const moreActions = useMemo<MobileTopNavProps["moreActions"]>(() => {
    if (isMainNav) return undefined
    const actions: NonNullable<MobileTopNavProps["moreActions"]> = []
    if (isCommunityDetail) {
      actions.push({
        label: "Share",
        onClick: () => navigator.clipboard.writeText(window.location.href),
      })
    }
    if (/^\/coaching-sessions\/[^/]+$/.test(currentPath)) {
      actions.push({
        label: "Share",
        onClick: () => navigator.clipboard.writeText(window.location.href),
      })
    }
    return actions.length > 0 ? actions : undefined
  }, [currentPath, isMainNav, isCommunityDetail])

  const inlineActions = useMemo<MobileTopNavProps["inlineActions"]>(() => {
    if (isMainNav) return undefined
    const actions: NonNullable<MobileTopNavProps["inlineActions"]> = []

    if (isRitualDetail || isCommunityDetail || isProfilePage || isOtherProfilePage) {
      actions.push({
        icon: "share",
        onClick: () => setShareOpen(true),
      })
    }

    return actions.length > 0 ? actions : undefined
  }, [isMainNav, isRitualDetail, isCommunityDetail, isProfilePage, isOtherProfilePage])

  return (
    <div className="relative min-h-screen">
      <div className="mx-auto">
        {showBackground && (
          <div
            className={`absolute top-0 left-0 right-0 h-122.75 -z-10 w-full ${
              isDashboardRoute
                ? "bg-cover bg-no-repeat bg-center md:bg-[position:185%] "
                : "bg-cover md:bg-[length:150%_100%] bg-no-repeat bg-center md:bg-[position:185%]"
            }`}
            style={{
              backgroundImage: `url(${backgroundImage})`,
            }}
          >
            {isDashboardRoute && (
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(180deg, rgba(2, 2, 2, 0.27) 0%, #020202 100%)",
                }}
              />
            )}

            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(180deg, rgba(2, 2, 2, 0) 0%, #020202 100%)" }}
            />

            {isAltRoute && (
              <div
                className="absolute inset-0 backdrop-blur-2xl"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(2, 2, 2, 0.6) 100%, #020202 100%, #020202 100%)",
                }}
              />
            )}
          </div>
        )}

        <ScrollToTop />

        <div className="hidden md:block">
          <AppHeader
            onSearchOpen={() => handleSearchOpen(true)}
            onNotificationsOpen={handleNotificationsOpen}
            onCreateOpen={() => handleCreateOpen(true)}
          />
        </div>

        {!isChatsRoute && (
          <div className="md:hidden">
            <MobileTopNav
              variant={isMyRitualityRoute ? "my-rituality" : isMainNav ? "main" : "back-more"}
              onSearch={() => handleSearchOpen(true)}
              onNotifications={() => handleNotificationsOpen(true)}
              onCreate={() => handleCreateOpen(true)}
              moreActions={moreActions}
              inlineActions={inlineActions}
            />
          </div>
        )}

        <div className="pt-[20px] pb-[85px] md:pt-[70px] md:pb-0">{children || <Outlet />}</div>

        <div className="md:hidden">
          <MobileBottomBar />
        </div>

        <NotificationsSheet open={notificationsOpen} onOpenChange={handleNotificationsOpen} />
        <SearchOverlay open={searchOpen} onOpenChange={handleSearchOpen} />
        <CreateModal
          isOpen={createOpen}
          onClose={() => handleCreateOpen(false)}
          onNavigate={path => navigate(path)}
        />
        <ShareModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          shareUrl={currentPath}
          shareText=""
        />
      </div>
    </div>
  )
}
